import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';
import FPActionButton from 'UI/components/atoms/FPActionButton';
import FPIconButton from 'UI/components/atoms/FPIconButton';
import { SvgVerticalMenu } from 'UI/res/icons/filled';

import { useStyles } from './styles';

/** @typedef {import('types/app').FPMenuButtonProps} FPMenuButtonProps */

/**
 * @param {FPMenuButtonProps} props
 */
const FPButtonMenu = ({
  children,
  className = undefined,
  icon = undefined,
  IconButtonProps = {},
  isIconButton = false,
  menuItems = [],
  menuProps = {},
  onOpenChange = undefined,
  open = undefined,
  showDefaultIcon = undefined,
  text = '',
  title = 'More options',
  triggerRef = undefined,
  ...rest
}) => {
  const history = useHistory();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const isControlled = typeof open === 'boolean';
  const isOpen = isControlled ? open : Boolean(anchorEl);

  const handleOpenChange = nextOpen => {
    if (!nextOpen) setAnchorEl(null);
    if (onOpenChange) onOpenChange(nextOpen);
  };

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
    handleOpenChange(true);
  };
  const handleClose = () => handleOpenChange(false);

  const isEmptyMenu = menuItems.every(item => !item.visible);
  const verticalMenuSize = 16;

  return (
    !isEmptyMenu && (
      <>
        {isIconButton ? (
          <FPIconButton
            data-testid="menu-icon-button"
            ref={triggerRef}
            aria-haspopup="true"
            onClick={handleClick}
            icon={icon ?? SvgVerticalMenu}
            iconProps={!icon && { size: verticalMenuSize }}
            tooltipProps={{ placement: 'bottom', title }}
            {...IconButtonProps}
            {...rest}
          >
            {children}
          </FPIconButton>
        ) : (
          <FPActionButton
            text={text}
            icon={icon || (showDefaultIcon && SvgVerticalMenu)}
            iconProps={IconButtonProps}
            aria-haspopup="true"
            disabled={isEmptyMenu}
            onClick={handleClick}
            className={clsx(classes.startIcon, className)}
            {...rest}
          >
            {children}
          </FPActionButton>
        )}
        {!isEmptyMenu && (
          <Menu
            data-testid="fp-menu-list"
            anchorEl={anchorEl}
            open={isOpen}
            getContentAnchorEl={null}
            onClose={handleClose}
            {...menuProps}
          >
            {menuItems.map(
              item =>
                item.visible && (
                  <MenuItem
                    key={item.key || item.title}
                    aria-label={item.title}
                    className={clsx(
                      classes.root,
                      item.wrapper === 'a' && classes.communicationAction,
                      item.isDisabled && classes.disabledItem
                    )}
                    component={item.wrapper || 'li'}
                    href={item?.contactData?.href}
                    onClick={e => {
                      handleClose();
                      item.link ? history.push(item.link) : item.action && item.action(e);
                      item?.contactData?.onPhoneClick && item.contactData.onPhoneClick();
                    }}
                    target={item.target}
                    rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
                  >
                    {item.icon && <div className={classes.iconMargin}>{item.icon}</div>}
                    <div>
                      {item.title}
                      {item?.description && (
                        <Typography component="span" variant="body2" color="textSecondary">
                          <br />
                          {item.description}
                        </Typography>
                      )}
                    </div>
                  </MenuItem>
                )
            )}
          </Menu>
        )}
      </>
    )
  );
};

export default FPButtonMenu;
